import { useCallback, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/legacy/image'

import clsx from 'clsx'

// Layouts
import CarRentalLayout from '@/layouts/carRentalLayout'
import styles from './Cars.module.css'

// Components
import Head from '@/components/Head'
import Benefits from '@/components/Benefits'
import CollapsibleQuestion from '@/v2/common/CollapsibleQuestion'

import { SimpleSearchForm } from '@/components/cars/SearchForm'

// Logic
import * as Criteria from '@/logic/CarCriteria'

// Assets
import Vroom from '@/assets/img/cars/svg/vroom.png'
import Ada from '@/assets/img/cars/svg/renters/ada.svg'
import Avis from '@/assets/img/cars/svg/renters/avis.svg'
import Europcar from '@/assets/img/cars/svg/renters/europcar.svg'
import Budget from '@/assets/img/cars/svg/renters/budget.svg'
import Sixt from '@/assets/img/cars/svg/renters/sixt.svg'
import Hertz from '@/assets/img/cars/svg/renters/hertz.svg'

function areCriteriaValid(criteria) {
  return criteria?.origin &&
    criteria?.destination &&
    criteria?.pickup_date &&
    criteria?.return_date
    ? true
    : false
}

function areCriteriaEquals(left, right) {
  if (left === null && right === null) {
    return true
  }
  if (left === null || right === null) {
    return false
  }

  return (
    left.origin === right.origin &&
    left.destination === right.destination &&
    left.pickup_date?.getTime() === right.pickup_date?.getTime() &&
    left.return_date?.getTime() === right.return_date?.getTime()
  )
}

function Home() {
  const { query, push } = useRouter()

  const [criteria, setCriteria] = useState(null)
  const updateCriteria = useCallback(
    (update) =>
      setCriteria((current) => {
        if (update) {
          const newCriteria = current ? { ...current, ...update } : update
          newCriteria.attempts = 0 // reset the number of attempts

          if (
            areCriteriaValid(newCriteria) &&
            !areCriteriaEquals(current, newCriteria)
          ) {
            const params = Criteria.createParamsFromCriteria(newCriteria)

            // first we adapt the query
            push('/cars/search?' + params, null, {
              shallow: true,
              scroll: false,
            })

            return newCriteria
          }
        }

        // no change
        return current
      }),
    [setCriteria]
  )

  // creates the criteria from the query
  useEffect(
    () => updateCriteria(Criteria.createCriteriaFromQuery(query)),
    [query]
  )

  const defaultPickupDate = new Date()
  defaultPickupDate.setDate(defaultPickupDate.getDate() + 30)
  defaultPickupDate.setHours(9, 0, 0, 0) // Set time to 9:00 AM
  const defaultPickupDateFormatted = defaultPickupDate.toISOString()

  const defaultReturnDate = new Date()
  defaultReturnDate.setDate(defaultReturnDate.getDate() + 37)
  defaultReturnDate.setHours(18, 0, 0, 0) // Set time to 6:00 PM
  const defaultReturnDateFormatted = defaultReturnDate.toISOString()

  return (
    <>
      <main className="bg-[#F3F4F9]">
        <Head
          title={'Location de voiture dans plus de 190 pays'}
          description={
            'Location de voiture pour vos vacances dans 190 pays avec nos 20 000 agences partenaires à travers le monde. Profitez de notre service client d’exception et réservez facilement en ligne !'
          }
        />
        <header
          className={`pt-[290px] md:pt-[200px] pb-250 relative bg-[#3791C4] ${styles.hero}`}
        >
          <div
            className={clsx(
              'container relative flex flex-col justify-start gap-40'
            )}
          >
            <div className="row">
              <div className="col-12 text-white text-left">
                <h1 className="text-50 text-center md:text-left md:text-60 lg:text-70 font-bold mx-auto leading-none">
                  Louez une voiture pour vos vacances
                </h1>
                <h2 className="font-bold text-18 text-center md:text-left md:text-20 lg:text-22 mx-auto leading-none">
                  simplement, sereinement, joyeusement.
                </h2>
              </div>
            </div>

            {!criteria && (
              <div className="row">
                <div className="col-12 flex items-center justify-center">
                  <SimpleSearchForm
                    className={
                      'p-20 relative flex flex-col lg:flex-row w-full gap-10 bg-white rounded-15 shadow-sm items-stretch'
                    }
                    updateCriteria={updateCriteria}
                  />
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-12 flex items-center gap-20 flex-wrap justify-around lg:justify-start">
                <Ada />
                <Avis />
                <Europcar />
                <Budget />
                <Sixt />
                <Hertz />
              </div>
              <div className="col-12 mt-30 md:mt-[16px]">
                <Benefits
                  wording={[
                    'Annulable gratuitement 48h à l’avance',
                    'Service client disponible 7J/7',
                    'Payez avec vos chèques vacances',
                  ]}
                  hideSeparator={true}
                  className="justify-around lg:justify-start text-white font-bold drop-shadow gap-0 sm:gap-10 md:gap-20"
                />
              </div>
            </div>
          </div>
        </header>

        <div
          style={{
            background:
              'linear-gradient(180deg, #E1BDC5 0%, rgba(247, 255, 151, 0.47) 100%)',
          }}
        >
          <div className="container pt-50">
            <div className="row">
              <div className="col-12 text-center">
                <div className="max-w-[500px]">
                  <Image
                    src={Vroom}
                    alt=""
                    layout="responsive"
                    objectFit="contain"
                  />
                </div>

                <div className="w-full h-[1px] bg-noir-20 mt-50 mb-25" />
              </div>
              <div className="col-12 md:col-4 py-25">
                <h3 className="text-26 text-dark-100">
                  Est-ce que rouler
                  <br /> c’est tromper ?{' '}
                </h3>
                <p className="text-17 text-dark-80 mt-20">
                  La location de voiture c’est un super pouvoir de plus, mais si
                  avant de rouler vous voulez voler, pour réserver vos billets,{' '}
                  <a className="underline" href="/">
                    c’est par ici
                  </a>
                  .
                </p>
              </div>

              <div className="col-12 md:col-4 py-25">
                <h3 className="text-26 text-dark-100">
                  Sans pub et
                  <br /> options inutiles.
                </h3>
                <p className="text-17 text-dark-80 mt-20">
                  Pas de publicités, pas de frais cachés : juste un site clair
                  comme de l’eau de roche pour réserver tous vos billets d’avion
                  en 2 temps, 3 mouvements.
                </p>
              </div>

              <div className="col-12 md:col-4 py-25">
                <h3 className="text-26 text-dark-100">
                  Le (super)
                  <br /> service client.
                </h3>
                <p className="text-17 text-dark-80 mt-20">
                  Notre truc à nous ? Un service client basé en France pour vous
                  conseiller ou vous assister en cas de problème. Vous avez un
                  doute ? Jetez un oeil{' '}
                  <a
                    className="underline"
                    href="https://www.trustpilot.com/review/ulysse.com"
                  >
                    par ici
                  </a>{' '}
                  !
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-40 pb-40">
                <div className="md:col-span-3">
                  <h2 className="text-24 font-bold mb-4">
                    Location voiture avec Ulysse dans tous les aéroports
                  </h2>
                </div>
                <div className="md:col-span-9 grid grid-cols-1 md:grid-cols-3 gap-10">
                  <div>
                    <h3 className="text-18 font-semibold mb-2">Top pays</h3>
                    <ul className="text-14 text-dark-80 mt-10">
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=CDG&destination=CDG&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture en France
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=FCO&destination=FCO&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture en Italie
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=BCN&destination=BCN&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture en Espagne
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=BER&destination=BER&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture en Allemagne
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=LIS&destination=LIS&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture au Portugal
                          </a>
                        </h4>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-18 font-semibold mb-2">
                      Top aéroports en Europe
                    </h3>
                    <ul className="text-14 text-dark-80 mt-10">
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=CDG&destination=CDG&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Paris
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=BCN&destination=BCN&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Barcelone
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=BER&destination=BER&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Berlin
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=LHR&destination=LHR&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Londres
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=FCO&destination=FCO&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Rome
                          </a>
                        </h4>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-18 font-semibold mb-2">
                      Top aéroports en France
                    </h3>
                    <ul className="text-14 text-dark-80 mt-10">
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=NCE&destination=NCE&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Nice
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=TLS&destination=TLS&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Toulouse
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=AJA&destination=AJA&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Ajaccio
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=MRS&destination=MRS&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Marseille
                          </a>
                        </h4>
                      </li>
                      <li>
                        <h4>
                          <a
                            className="hover:underline"
                            href={`/cars/search?origin=BOD&destination=BOD&pickup_date=${defaultPickupDateFormatted}&return_date=${defaultReturnDateFormatted}`}
                          >
                            Location voiture aéroport Bordeaux
                          </a>
                        </h4>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-12 pb-20">
                <CollapsibleQuestion
                  title={
                    'Location voiture pour ses vacances : pourquoi Ulysse ?'
                  }
                >
                  <div className="flex flex-col text-14 text-dark-80 gap-10">
                    <p>
                      Avec des milliers de voiture dans plus de 160 pays, Ulysse
                      est le choix le plus abordable pour louer une voiture
                      proche d’un aéroport pour de nombreuses personnes en
                      Europe et au-delà. En effet, Ulysse est une agence de
                      location de voitures qui propose des services de qualité
                      dans les aéroports de Paris et dans d’autres villes du
                      monde. Si vous êtes en voyage touristique à Paris, Nice,
                      Lyon ou Ajaccio et que vous cherchez une voiture de
                      location à un budget raisonnable, Ulysse est l’une des
                      meilleures options disponibles.
                    </p>

                    <p>
                      Louer une voiture avec Ulysse est simple et facile, avec
                      un service client en France et une note Trustpilot de plus
                      de 4.8/5 avec plus de 3000 avis. Nous sommes classés
                      numéro 1 dans la catégorie agence de voyage, et c’est
                      grâce à vous.
                    </p>

                    <p>
                      Nous proposons une large gamme de véhicules, y compris des
                      voitures compactes idéales pour la ville, ainsi que des
                      voitures plus grandes pour les voyages en famille. Que
                      vous soyez en voyage d’affaires ou en vacances, Ulysse
                      aura forcément une voiture qui répondra à vos besoins de
                      location.
                    </p>

                    <p>
                      En tant que partenaire travaillant avec la plupart des
                      agences de location de voitures, Ulysse vous offre un
                      service de location de voitures transparent, rapide,
                      pratique et flexible. Parmi la large gamme de location
                      voiture disponible, vous pouvez retrouver :
                    </p>

                    <ul>
                      <li>
                        Des voitures standard : avec des modèles économiques,
                        des compactes, des berlines (Audi, Mercedes, BMW…), des
                        SUV ainsi que des modèles pour les familles nombreuses.
                      </li>
                      <li>
                        - La location de voiture spécifique : comme les modèles
                        hybrides de la marque Toyota, les modèles de voitures
                        électriques de la marque Tesla.
                      </li>
                      <li>
                        - La location d’utilitaires tels que : des minivans, des
                        camionnettes ou des camions pour partir en road trip en
                        famille depuis n’importe quel aéroport.
                      </li>
                    </ul>

                    <p>
                      Outre des véhicules de qualité et une assistance
                      professionnelle dévouée, Ulysse vous offre toute une série
                      d’avantages, notamment : une plateforme de réservation en
                      ligne sécurisée, des options supplémentaires utiles
                      (sièges bébé, conducteur supplémentaire, GPS, etc.) et la
                      possibilité de payer avec vos chèques vacances.
                    </p>

                    <p>
                      Alors il n’y a plus qu’à réserver votre voiture et roulez
                      jeunesse.
                    </p>
                  </div>
                </CollapsibleQuestion>

                <CollapsibleQuestion
                  title={
                    'Pourquoi Ulysse est le meilleur comparateur de location de voiture ?'
                  }
                >
                  <div className="flex flex-col text-14 text-dark-80 gap-10">
                    <p>
                      Avant toute chose, utiliser un comparateur de location de
                      voiture est la meilleure chose à faire pour vos vacances.
                      Cela vous permettra avant tout de gagner un temps
                      précieux.
                    </p>

                    <p>
                      Plutôt que de visiter plusieurs sites web d’agences de
                      location, vous pouvez accéder à toutes nos offres
                      disponibles en quelques clics seulement. Non seulement
                      cela simplifie le processus de recherche, mais cela vous
                      aide également à économiser de l’argent (et le temps c’est
                      de l’argent).
                    </p>

                    <p>
                      Ulysse va afficher les prix proposés par plus de 900
                      agences, ce qui vous permet de comparer facilement et de
                      choisir la meilleure offre disponible.
                    </p>

                    <p>
                      L’autre avantage, c’est qu’on vous donne les notes de
                      chaque agence d’un seul coup d’oeil, pour éviter les
                      mauvaises surprises le jour J. Personne n’a envie d’être
                      la dernière roue du carrosse, et c’est totalement normal.
                    </p>

                    <p>
                      Enfin et ce n’est pas rien, vous profitez de notre super
                      service-client (basé en France) qui est le meilleur depuis
                      des années. Ce n’est pas nous qui le disons, c’est nos{' '}
                      <a
                        className="underline"
                        href="https://fr.trustpilot.com/review/ulysse.com"
                      >
                        3000 avis Trustpilot
                      </a>{' '}
                      qui nous donnent une moyenne de 4.8/5, faisant d’Ulysse
                      l’agence de voyage la mieux notée du secteur.
                    </p>

                    <p>
                      Et si vous partiez sereinement en vacances dès que vous
                      posez un pied à l’aéroport grâce à Ulysse ?
                    </p>
                  </div>
                </CollapsibleQuestion>

                <CollapsibleQuestion
                  title={
                    'Utiliser mes chèques vacances ANCV pour louer une voiture, c’est possible ?'
                  }
                >
                  <div className="flex flex-col text-14 text-dark-80 gap-10">
                    <p>C’est nouveau et ça vient de sortir.</p>
                    <p>
                      Toutes les agences qui ne font que de la location de
                      voiture n’accepte pas forcément les chèques vacances, il
                      faut donc se renseigner pour chaque agence.
                    </p>
                    <p>
                      Avec Ulysse, c’est terminé ! Peu importe la voiture que
                      vous souhaitez louer, vos chèques vacances seront acceptés
                      et vous pourrez louer votre voiture en quelques clics.
                    </p>
                    <p>Le process de réservation est simple :</p>
                    <p>
                      1 -{' '}
                      <strong>
                        Payez votre location avec votre CB sur notre site en 3
                        min
                      </strong>
                    </p>
                    <p>
                      2 -{' '}
                      <strong>
                        Remplissez notre formulaire ANCV Connect en 1 min
                      </strong>
                    </p>
                    <p>
                      3 -{' '}
                      <strong>Vous êtes remboursés sous 5 jours ! 🥳</strong>
                    </p>
                    <p>
                      Vous pouvez retrouver plus d’informations directement sur
                      nos pages dédiées pour{' '}
                      <a
                        className="underline"
                        href="https://ulysse.com/ancv-connect"
                      >
                        les chèques vacances Connect
                      </a>{' '}
                      et les{' '}
                      <a className="underline" href="https://ulysse.com/ancv">
                        chèques vacances Classic
                      </a>
                    </p>
                    <p>
                      En choisissant d’utiliser vos{' '}
                      <strong>chèques vacances</strong> chez des agences
                      locales, vous contribuez indirectement au soutien des
                      entreprises touristiques établies en France. Cet acte
                      renforce le tissu économique local et encourage encore
                      plus les entreprises à proposer ce type de paiement,
                      créant un cercle vertueux bénéfique pour tous les acteurs
                      du secteur.
                    </p>
                    <p>
                      On vous remercie donc d’avance pour votre soutien et on
                      vous souhaite bonne route.
                    </p>
                    <p></p>
                  </div>
                </CollapsibleQuestion>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

Home.layout = CarRentalLayout
export default Home
